import React, { useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Grid,
  Stack,
  Avatar,
  Divider,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import { Verified } from '@mui/icons-material';
import HomeHeader from '../../../../Components/HomeHeader';
import HomeFooter from '../../../../Components/HomeFooter';
import { professionalTheme } from './themes';
import ApprovalStampDrawer from './ApprovalStampDrawer';
import InvoiceActions from './invoiceactions';
import PaymentCTA from './paymentCTA';

/**
 * BlitzPayInvoiceDesign
 *
 * Data priority for "Billed From" (the sender / Blitz identity, top-right):
 *
 *   Per-invoice snapshot from admin dialog (only trusted when _confirmed === true):
 *     1. billed_from_info.sender_display_name  ← display-name override
 *     2. billed_from_info.company_name         ← company name override
 *     3. billed_from_info.address_*            ← address override
 *
 *   Active BlitzPay company profile:
 *     4. blitzpayProfile.sender_display_name / invoice_sender_name / company_name
 *
 *   ⚠️  The _confirmed guard:
 *   Old invoices may have corrupt data in billed_from_info (client/"billed_to"
 *   data accidentally written there by an earlier version of the admin dialog).
 *   We only trust billed_from_info identity/address fields when the object
 *   carries { _confirmed: true }, which is written by the current admin dialog.
 *   Without that flag the field is treated as absent and the active BlitzPay
 *   profile becomes the next source of truth before defaults.
 *
 *   Hardcoded fallback (always last resort):
 *     5. BLITZ_DEFAULTS  (legacy remittance profile fallback)
 *
 * Data priority for "Billed To" (who owes payment, left column):
 *   1. invoice.custom_details.billed_to_info  (set by admin dialog)
 *   2. invoice.client_name / invoice.client_email
 *   3. billedToName / billedToEmail props
 */

const BLITZ_DEFAULTS = {
  company_name:  'Pangea Omninational Corporation',
  address_line1: '8724 Cavell Lane',
  city:          'Houston',
  state:         'TX',
  zip_code:      '77055',
  country:       'USA',
  email:         'win@useblitz.co',
  terms_and_conditions: 'Make all checks payable to "DBA Pangea Omninational Corporation"',
  bank_details: {
    account_holder: 'Houston Alexander Roa',
    account_name:   'Pangea',
    bank_name:      'Prosperity Bank',
    bank_address:   '2117 N. Fry Road, Katy, TX 77449',
    account_number: '217841714',
    routing_number: '113122655',
    swift_code:     'PROYUS44',
  },
};

const hasRenderableBankDetails = (details = {}) => Boolean(
  details.bank_name ||
  details.account_number ||
  details.routing_number
);

const LEGACY_GENERIC_TERMS = 'Make all checks payable to the company listed above.';

const getProfileTerms = (terms) => (
  terms && terms !== LEGACY_GENERIC_TERMS ? terms : ''
);

const getProfileSenderDisplayName = (profile = {}) => (
  profile?.sender_display_name ||
  profile?.invoice_sender_name ||
  profile?.company_name ||
  ''
);

const getProfileLegalCompanyName = (profile = {}) => (
  profile?.company_name ||
  profile?.invoice_sender_name ||
  profile?.sender_display_name ||
  ''
);

const getPayableTerms = (companyName) => (
  companyName
    ? `Make all checks payable to "${companyName}"`
    : BLITZ_DEFAULTS.terms_and_conditions
);

const BlitzPayInvoiceDesign = ({
  invoice,
  user,
  company,
  companyInfo,
  invoiceSettings,
  blitzpayProfile,
  payouts,
  onDownloadPDF,
  onCopyInvoiceNumber,
  pdfGenerating,
  formatDate,
  getStatusColor,
  getStatusText,
  getEffectiveValue,
  getInvoiceNotes,
  getInvoiceTerms,
  daysLate,
  totalDaysToPay,
  calculatedDueDate,
  supportingDocument,
  needsApproval,
  userInfo,
  onOpenApprovalDialog,
  onOpenLoginDialog,
  billedToEmail,
  billedToName,
  isAuthenticated = false,
}) => {
  const customDetails  = invoice?.custom_details || {};
  const billedFromInfo = customDetails.billed_from_info || {};
  const billedToInfo   = customDetails.billed_to_info   || {};
  const vendorIdInfo   = customDetails.vendor_id_info   || {};
  const additionalInfo = {
    ...(customDetails.additional_info || {}),
    ...(billedToInfo.additional_info || {}),
  };
  const invoiceBankDetails = {
    ...(customDetails.additional_info?.bank_details || {}),
    ...(billedToInfo.bank_details || {}),
  };

  /**
   * _confirmed guard
   *
   * billed_from_info is only trusted for address/identity fields when the
   * admin dialog has explicitly confirmed it by setting _confirmed: true.
   * Pre-existing records without this flag may contain corrupt client data
   * (UMG/Sony/etc. billed_to data mistakenly written to billed_from_info by
   * an older version of the dialog). In that case we fall straight to
   * BLITZ_DEFAULTS, which is always the safe fallback for the sender identity.
   */
  const bfiTrusted = billedFromInfo._confirmed === true ? billedFromInfo : {};

  const mergedData = useMemo(() => {
    const profileSenderDisplayName = getProfileSenderDisplayName(blitzpayProfile);
    const profileLegalCompanyName = getProfileLegalCompanyName(blitzpayProfile);

    // ── Resolve display name (vendor AP name) ────────────────────────────
    // Priority: confirmed per-invoice override > active profile > defaults
    const resolvedSenderName =
      bfiTrusted.sender_display_name         ||
      bfiTrusted.company_name                ||
      profileSenderDisplayName               ||
      BLITZ_DEFAULTS.company_name;

    const legalCompanyName =
      bfiTrusted.company_name        ||
      profileLegalCompanyName        ||
      BLITZ_DEFAULTS.company_name;

    const billedFrom = {
      display_name:  resolvedSenderName,
      company_name:  legalCompanyName,
      has_display_name_override: resolvedSenderName !== legalCompanyName,

      // Address/identity for sender must come from confirmed billed_from_info.
      // If absent, use Blitz defaults to avoid leaking billed-to identity.
      address_line1: bfiTrusted.address_line1 || BLITZ_DEFAULTS.address_line1,
      address_line2: bfiTrusted.address_line2 || '',
      city:          bfiTrusted.city          || BLITZ_DEFAULTS.city,
      state:         bfiTrusted.state         || BLITZ_DEFAULTS.state,
      zip_code:      bfiTrusted.zip_code      || BLITZ_DEFAULTS.zip_code,
      country:       bfiTrusted.country       || BLITZ_DEFAULTS.country,
      email:         bfiTrusted.email         || BLITZ_DEFAULTS.email,
      phone:         bfiTrusted.phone         || null,
      tax_id:        bfiTrusted.tax_id        || null,
    };

    const vendor = {
      primary_vendor_id: blitzpayProfile?.vendor_info?.vendor_id  || vendorIdInfo.primary_vendor_id || null,
      erp_system_name:   blitzpayProfile?.vendor_info?.erp_system || vendorIdInfo.erp_system_name   || null,
    };

    const profileBankDetails = blitzpayProfile?.bank_details || {};
    const hasInvoiceBankDetails = hasRenderableBankDetails(invoiceBankDetails);
    const hasFullProfileBankDetails = Boolean(
      profileBankDetails.account_holder ||
      profileBankDetails.account_name ||
      profileBankDetails.account_number ||
      profileBankDetails.routing_number ||
      profileBankDetails.swift_code ||
      profileBankDetails.iban
    );
    const bankDetails = hasInvoiceBankDetails
      ? invoiceBankDetails
      : (hasFullProfileBankDetails ? profileBankDetails : BLITZ_DEFAULTS.bank_details);

    const bank = {
      account_holder: bankDetails.account_holder || null,
      account_name:   bankDetails.account_name   || null,
      bank_name:      bankDetails.bank_name      || null,
      bank_address:   bankDetails.bank_address   || null,
      account_number: bankDetails.account_number || null,
      routing_number: bankDetails.routing_number || null,
      swift_code:     bankDetails.swift_code     || bankDetails.swift || null,
    };

    const additional = {
      logo_url:             blitzpayProfile?.logo_url                    || additionalInfo.logo_url             || companyInfo?.logo || null,
      website:              'https://www.useblitz.co',
      payment_instructions: blitzpayProfile?.terms?.payment_instructions || additionalInfo.payment_instructions || '',
      terms_and_conditions: getProfileTerms(additionalInfo.terms_and_conditions) || getProfileTerms(blitzpayProfile?.terms?.default_terms) || getPayableTerms(legalCompanyName || resolvedSenderName),
      notes:                blitzpayProfile?.terms?.invoice_notes        || additionalInfo.notes                || '',
    };

    /**
     * Billed To — the company that owes us money.
     * Priority: billed_to_info (set by admin) > top-level invoice fields > props
     */
    const fallbackContactName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    const billedTo = {
      company_name:  billedToInfo.company_name  || invoice?.client_name  || billedToName  || user?.company_name || company?.name || '',
      contact_name:  billedToInfo.contact_name  || billedToName           || fallbackContactName || '',
      email:         billedToInfo.email         || invoice?.client_email  || billedToEmail || user?.email || '',
      phone:         billedToInfo.phone         || '',
      tax_id:        billedToInfo.tax_id        || '',
      address_line1: billedToInfo.address_line1 || '',
      address_line2: billedToInfo.address_line2 || '',
      city:          billedToInfo.city          || '',
      state:         billedToInfo.state         || '',
      zip_code:      billedToInfo.zip_code      || '',
      country:       billedToInfo.country       || '',
    };

    return { billedFrom, vendor, bank, additional, billedTo };
  }, [
    blitzpayProfile, bfiTrusted, billedToInfo, vendorIdInfo,
    additionalInfo, invoiceBankDetails, invoice, billedToEmail, billedToName, companyInfo, user, company,
  ]);

  const safePayouts = Array.isArray(payouts) ? payouts.filter(p => p != null) : [];
  const safeAmount  = (amt) => (typeof amt === 'number' ? amt : 0);
  const parseMoney  = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const firstFiniteNumber = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined || candidate === '') continue;
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };
  const amountDue   = safeAmount(invoice?.amount_due);
  const amountPaid  = Math.min(
    Math.max(firstFiniteNumber(customDetails?.amount_paid) ?? 0, 0),
    amountDue
  );
  const outstanding = Math.max(amountDue - amountPaid, 0);
  // Priority: canonical subtotal from custom_details → invoice_items sum → payout amounts → amountDue.
  // Avoids showing a grossed-up payout.amount when the rail fee is baked into individual amounts.
  const canonicalSubtotal = firstFiniteNumber(
    customDetails?.subtotal_total,
    customDetails?.fee_breakdown?.subtotal_total,
  );
  const displayedSubtotal = canonicalSubtotal !== null
    ? canonicalSubtotal
    : invoice?.invoice_items?.length > 0
      ? invoice.invoice_items.reduce((sum, item) => sum + parseMoney(item?.amount || 0), 0)
      : safePayouts.length > 0
        ? safePayouts.reduce((sum, payout) => sum + parseMoney(payout?.amount || payout?.net_amount || 0), 0)
        : amountDue;
  const explicitBlitzPayFee = firstFiniteNumber(
    customDetails?.blitzpay_fee_amount,
    customDetails?.fee_breakdown?.blitzpay_fee,
    customDetails?.blitzpay_fee,
  );
  const fallbackBlitzPayFee = Math.max(amountDue - displayedSubtotal, 0);
  const normalizedExplicitBlitzPayFee = explicitBlitzPayFee !== null
    ? Math.max(explicitBlitzPayFee, 0)
    : null;
  const blitzPayFee = normalizedExplicitBlitzPayFee !== null && normalizedExplicitBlitzPayFee > 0.009
    ? normalizedExplicitBlitzPayFee
    : (fallbackBlitzPayFee > 0.009 ? fallbackBlitzPayFee : 0);
  const blitzPayFeePercent = firstFiniteNumber(
    customDetails?.blitzpay_fee_percentage,
    customDetails?.fee_breakdown?.blitzpay_fee_percentage,
    invoice?.fee_percentage,
  );
  // Detect creator-pays-fee scenario where blitzpay_fee fields are 0
  const feePayer = customDetails?.fee_payer || null;
  const actualFeePercentage = firstFiniteNumber(customDetails?.actual_fee_percentage);
  const actualFeeAmount = firstFiniteNumber(customDetails?.actual_fee_amount);

  // When creator pays, blitzpay_fee_* fields are 0 but actual_fee_* has the real values
  const effectiveBlitzPayFee = blitzPayFee > 0.009
    ? blitzPayFee
    : (actualFeeAmount && actualFeeAmount > 0.009 ? actualFeeAmount : 0);
  const effectiveBlitzPayFeePercent = (blitzPayFeePercent && blitzPayFeePercent > 0)
    ? blitzPayFeePercent
    : actualFeePercentage;

  const blitzPayFeeLabel = effectiveBlitzPayFeePercent && effectiveBlitzPayFeePercent > 0
    ? `BlitzPay Fee (${effectiveBlitzPayFeePercent}%)${feePayer === 'creator' ? ' — Creator covers' : ''}`
    : 'BlitzPay Fee';

  const effectiveInvoiceNumber = getEffectiveValue('invoice_number', invoice?.po_number);
  const effectiveDateIssued    = getEffectiveValue('date_of_issue', invoice?.created_at);
  const blitzInvoiceId = invoice?.id;

  const paymentDate   = invoice?.payment_date;
  const invoiceStatus = invoice?.status || invoice?.invoice_status;
  const externalPO    = invoice?.external_po_number;
  const trimmedExternalPO = typeof externalPO === 'string' ? externalPO.trim() : externalPO;
  const referenceValue = trimmedExternalPO || invoice?.notes;
  const invoiceNoteValue = typeof invoice?.notes === 'string' ? invoice.notes.trim() : '';

  let invoiceNotes = mergedData.additional.notes || getInvoiceNotes();
  let invoiceTerms = mergedData.additional.terms_and_conditions || getInvoiceTerms();
  const paymentInstructions = mergedData.additional.payment_instructions;
  if (paymentInstructions) {
    invoiceNotes = invoiceNotes ? `${invoiceNotes}\n\n${paymentInstructions}` : paymentInstructions;
  }
  const hasExternalPOValue = typeof trimmedExternalPO === 'string'
    ? trimmedExternalPO.length > 0
    : Boolean(trimmedExternalPO);
  const shouldShowStandaloneInvoiceNote = Boolean(
    hasExternalPOValue &&
    invoiceNoteValue &&
    (typeof invoiceNotes !== 'string' || !invoiceNotes.includes(invoiceNoteValue))
  );

  const isAlreadyApproved        = invoice?.status === 'approved' || invoice?.status === 'paid' || invoice?.approval_proof;
  const shouldShowApprovalBanner = needsApproval && !isAlreadyApproved;
  const hasBilledToAddress       = mergedData.billedTo.address_line1 || mergedData.billedTo.city;

  const handlePrintInvoice = async () => {
    const invoiceEl = document.getElementById('invoice-content');
    if (!invoiceEl) return;

    const printFrame = document.createElement('iframe');
    printFrame.setAttribute('title', 'BlitzPay Invoice Print Frame');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    printFrame.style.pointerEvents = 'none';

    document.body.appendChild(printFrame);

    const printDoc = printFrame.contentWindow?.document;
    if (!printDoc) {
      document.body.removeChild(printFrame);
      return;
    }

    const headMarkup = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    )
      .map((node) => node.outerHTML)
      .join('\n');

    printDoc.open();
    printDoc.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>BlitzPay Invoice Print</title>
          ${headMarkup}
          <style>
            html, body {
              margin: 0;
              padding: 0;
              background: #ffffff;
            }

            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            #invoice-print-root {
              display: flex;
              justify-content: center;
              align-items: flex-start;
              padding: 12mm;
              box-sizing: border-box;
              background: #ffffff;
            }

            #invoice-content {
              margin: 0 auto !important;
              box-shadow: none !important;
              background: #ffffff !important;
            }

            @page {
              size: auto;
              margin: 0;
            }

            @media print {
              html, body {
                background: #ffffff !important;
              }

              #invoice-print-root {
                padding: 12mm;
              }

              #invoice-content {
                overflow: visible !important;
              }
            }
          </style>
        </head>
        <body>
          <div id="invoice-print-root">${invoiceEl.outerHTML}</div>
        </body>
      </html>
    `);
    printDoc.close();

    const waitForImages = () => {
      const images = Array.from(printDoc.images || []);
      if (!images.length) return Promise.resolve();

      return Promise.all(
        images.map((image) => (
          image.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
              })
        )),
      );
    };

    try {
      await waitForImages();
    } catch (err) {
      console.warn('BlitzPay invoice print images did not fully load:', err);
    }

    setTimeout(() => {
      const frameWindow = printFrame.contentWindow;
      if (!frameWindow) {
        document.body.removeChild(printFrame);
        return;
      }

      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        printFrame.remove();
      };

      frameWindow.onafterprint = cleanup;
      frameWindow.focus();
      frameWindow.print();
      setTimeout(cleanup, 60000);
    }, 250);
  };

  return (
    <ThemeProvider theme={professionalTheme}>
      <Box sx={{ minHeight:'100vh', background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display:'flex', flexDirection:'column' }}>
        <HomeHeader />
        <Toolbar />

        {/* Action Bar */}
        <InvoiceActions
          onDownloadPDF={onDownloadPDF}
          onPrintInvoice={handlePrintInvoice}
          pdfGenerating={pdfGenerating}
          onCopyInvoiceNumber={onCopyInvoiceNumber}
          invoiceNumber={effectiveInvoiceNumber}
          invoiceStatus={invoiceStatus}
          createdDate={effectiveDateIssued}
          getStatusText={getStatusText}
          getStatusColor={getStatusColor}
          formatDate={formatDate}
          variant="blitzpay"
          isAuthenticated={isAuthenticated}
        />

        <Box sx={{ py:2, flex:1, maxWidth:'100vw', overflowX:'hidden' }}>

          {/* Approval Banner */}
          {shouldShowApprovalBanner && (
            <Fade in timeout={600}>
              <Box sx={{ width:{ xs:'95%', sm:'210mm' }, maxWidth:'100%', m:'0 auto', mb:2 }}>
                <Alert severity="warning" sx={{ borderRadius:3, '& .MuiAlert-message':{ width:'100%' } }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={600}>This BlitzPay invoice requires approval</Typography>
                    <Box sx={{ pl:2, py:1.5, borderLeft:'3px solid', borderColor:'warning.main', bgcolor:'rgba(237,108,2,0.08)', borderRadius:1 }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>Approver Details:</Typography>
                      <Typography variant="body2">Name: <strong>{mergedData.billedTo.contact_name || billedToName || 'Not specified'}</strong></Typography>
                      <Typography variant="body2">Email: <strong>{mergedData.billedTo.email || billedToEmail || 'Not specified'}</strong></Typography>
                    </Box>
                  </Stack>
                </Alert>
              </Box>
            </Fade>
          )}

          {/* Profile Badge */}
          {blitzpayProfile && (
            <Fade in timeout={400}>
              <Box sx={{ width:{ xs:'95%', sm:'210mm' }, maxWidth:'100%', m:'0 auto', mb:2 }}>
                <Alert severity="success" icon={<Verified />} sx={{ borderRadius:2 }}>
                  <Typography variant="body2" fontWeight={600}>Using Verified BlitzPay Company Profile</Typography>
                  <Typography variant="caption">
                    Billing information from {mergedData.billedFrom.display_name}
                    {mergedData.billedFrom.has_display_name_override && (
                      <> ({mergedData.billedFrom.company_name})</>
                    )}
                  </Typography>
                </Alert>
              </Box>
            </Fade>
          )}

          {/* ── MAIN INVOICE ─────────────────────────────────────────────── */}
          <Paper
            id="invoice-content"
            sx={{
              p:{ xs:2, sm:4 },
              width:{ xs:'95%', sm:'210mm' },
              maxWidth:'100%',
              minHeight:{ xs:'auto', sm:'297mm' },
              m:'0 auto',
              background:'white',
              position:'relative',
              overflow:'hidden',
              '@media print':{ p:4, width:'210mm', minHeight:'297mm', boxShadow:'none' },
            }}
          >
            {/* Top accent bar */}
            <Box sx={{ position:'absolute', top:0, left:0, right:0, height:6, background:'linear-gradient(90deg, #667eea, #764ba2)' }} />

            {/* ── HEADER ───────────────────────────────────────────────── */}
            <Grid container spacing={2} sx={{ mt:1, mb:3 }}>
              <Grid
                item
                xs={12}
                md={6}
                sx={{ '@media print': { flexBasis: '50%', maxWidth: '50%' } }}
              >
                {mergedData.additional.logo_url ? (
                  <img
                    src={mergedData.additional.logo_url}
                    alt={`${mergedData.billedFrom.display_name} Logo`}
                    style={{ maxWidth:140, maxHeight:60, marginBottom:8 }}
                  />
                ) : (
                  <Avatar sx={{ width:50, height:50, background:'linear-gradient(45deg, #667eea, #764ba2)', fontSize:'1.2rem', fontWeight:'bold', mb:1 }}>
                    {mergedData.billedFrom.display_name.charAt(0)}
                  </Avatar>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  textAlign:{ xs:'left', md:'right' },
                  '@media print': { flexBasis: '50%', maxWidth: '50%', textAlign: 'right' }
                }}
              >
                {/* Primary: display name (vendor AP name) */}
                <Typography variant="body2" sx={{ fontWeight:700, color:'#1e293b' }}>
                  {mergedData.billedFrom.display_name}
                </Typography>
                {/* Sub-line: legal company name when it differs from the display name */}
                {mergedData.billedFrom.has_display_name_override && (
                  <Typography variant="caption" display="block" color="textSecondary" sx={{ fontStyle:'italic' }}>
                    {mergedData.billedFrom.company_name}
                  </Typography>
                )}
                <Typography variant="caption" display="block" color="textSecondary">
                  {mergedData.billedFrom.address_line1}
                  {mergedData.billedFrom.address_line2 && `, ${mergedData.billedFrom.address_line2}`}
                </Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  {mergedData.billedFrom.city}, {mergedData.billedFrom.state} {mergedData.billedFrom.zip_code}
                </Typography>
                {mergedData.billedFrom.email && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    {mergedData.billedFrom.email}
                  </Typography>
                )}
                {mergedData.billedFrom.phone && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    {mergedData.billedFrom.phone}
                  </Typography>
                )}
                {mergedData.billedFrom.tax_id && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    Tax ID: {mergedData.billedFrom.tax_id}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my:2 }} />

            {/* ── INFO ROW ─────────────────────────────────────────────── */}
            <Grid container spacing={3} sx={{ mb:3 }}>

              {/* ── Billed To ──────────────────────────────────────────── */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{ '@media print': { flexBasis: '33.333333%', maxWidth: '33.333333%' } }}
              >
                <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700, textTransform:'uppercase' }}>Billed To</Typography>
                <Box sx={{ mt:0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight:700 }}>
                    {mergedData.billedTo.company_name || mergedData.billedTo.contact_name || 'N/A'}
                  </Typography>
                  {mergedData.billedTo.company_name && mergedData.billedTo.contact_name && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      Attn: {mergedData.billedTo.contact_name}
                    </Typography>
                  )}
                  {hasBilledToAddress && (
                    <>
                      <Typography variant="caption" display="block" color="textSecondary" sx={{ mt:0.5 }}>
                        {mergedData.billedTo.address_line1}
                        {mergedData.billedTo.address_line2 && `, ${mergedData.billedTo.address_line2}`}
                      </Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        {[mergedData.billedTo.city, mergedData.billedTo.state, mergedData.billedTo.zip_code]
                          .filter(Boolean).join(', ')}
                      </Typography>
                      {mergedData.billedTo.country && mergedData.billedTo.country !== 'USA' && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {mergedData.billedTo.country}
                        </Typography>
                      )}
                    </>
                  )}
                  {mergedData.billedTo.email && (
                    <Typography variant="caption" display="block" color="textSecondary" sx={{ mt:hasBilledToAddress ? 0.5 : 0 }}>
                      {mergedData.billedTo.email}
                    </Typography>
                  )}
                  {mergedData.billedTo.phone && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {mergedData.billedTo.phone}
                    </Typography>
                  )}
                  {mergedData.billedTo.tax_id && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      Tax ID: {mergedData.billedTo.tax_id}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* ── Invoice Dates ────────────────────────────────────────── */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{ '@media print': { flexBasis: '33.333333%', maxWidth: '33.333333%' } }}
              >
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Date of Issue</Typography>
                    <Typography variant="body2" sx={{ fontWeight:600 }}>{formatDate(effectiveDateIssued)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Due Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight:600 }}>
                      {paymentDate
                        ? formatDate(paymentDate)
                        : calculatedDueDate
                          ? formatDate(calculatedDueDate)
                          : formatDate(invoice?.created_at)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* ── Invoice Number & Amount Due ───────────────────────────── */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{ '@media print': { flexBasis: '33.333333%', maxWidth: '33.333333%' } }}
              >
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Blitz Transaction PO</Typography>
                    <Typography variant="body2" sx={{ fontWeight:600, fontFamily:'monospace' }}>
                      {effectiveInvoiceNumber || 'N/A'}
                    </Typography>
                  </Box>
                  {blitzInvoiceId ? (
                    <Box>
                      <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Blitz Invoice ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight:600, fontFamily:'monospace' }}>
                        {blitzInvoiceId}
                      </Typography>
                    </Box>
                  ) : null}
                  {mergedData.vendor.primary_vendor_id && (
                    <Box>
                      <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Vendor ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight:600, fontFamily:'monospace' }}>
                        {mergedData.vendor.primary_vendor_id}
                        {mergedData.vendor.erp_system_name && ` (${mergedData.vendor.erp_system_name})`}
                      </Typography>
                    </Box>
                  )}
                  {referenceValue && (
                    <Box>
                      <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Reference</Typography>
                      <Typography variant="body2" sx={{ fontWeight:600 }}>{referenceValue}</Typography>
                    </Box>
                  )}
                  <Box sx={{ bgcolor:'#fee2e2', p:1.5, borderRadius:1, mt:1, border:'1px solid #fecaca' }}>
                    <Typography variant="caption" sx={{ color:'#ef4444', fontWeight:700 }}>Amount Due (USD)</Typography>
                    <Typography variant="h4" sx={{ fontWeight:'bold', color:'#1e293b' }}>${outstanding.toFixed(2)}</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ my:2, borderStyle:'solid', borderWidth:1, borderColor:'#ef4444' }} />

            {/* ── ITEMS TABLE ──────────────────────────────────────────── */}
            <TableContainer sx={{ mb:3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor:'#f8fafc' }}>
                    <TableCell sx={{ fontWeight:700, color:'#1e293b', py:1 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight:700, color:'#1e293b', py:1 }}>Rate</TableCell>
                    <TableCell align="center" sx={{ fontWeight:700, color:'#1e293b', py:1 }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight:700, color:'#1e293b', py:1 }}>Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice?.invoice_items && invoice.invoice_items.length > 0 ? (
                    invoice.invoice_items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ py:1.5, whiteSpace:'pre-wrap', fontSize:'0.875rem' }}>{item?.description || 'Service'}</TableCell>
                        <TableCell align="right" sx={{ py:1.5, fontSize:'0.875rem' }}>${parseFloat(item?.rate || 0).toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ py:1.5, fontSize:'0.875rem' }}>{item?.quantity || 1}</TableCell>
                        <TableCell align="right" sx={{ py:1.5, fontWeight:600, fontSize:'0.875rem' }}>${parseFloat(item?.amount || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  ) : safePayouts.length > 0 ? (
                    safePayouts.map((payout, i) => {
                      const creatorName  = payout?.name || payout?.creator_info?.display_name || payout?.creator_id || `Vendor ${i + 1}`;
                      const payoutAmount = parseFloat(payout?.amount || payout?.net_amount || 0);
                      const description  = payout?.campaign_name
                        ? `${creatorName} — ${payout.campaign_name}`
                        : creatorName;
                      return (
                        <TableRow key={payout?.id || i}>
                          <TableCell sx={{ py:1.5, fontSize:'0.875rem' }}>
                            {description}
                            {payout?.creator_id && payout.name && (
                              <Typography variant="caption" display="block" color="text.secondary">@{payout.creator_id}</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py:1.5, fontSize:'0.875rem' }}>${payoutAmount.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ py:1.5, fontSize:'0.875rem' }}>1</TableCell>
                          <TableCell align="right" sx={{ py:1.5, fontWeight:600, fontSize:'0.875rem' }}>${payoutAmount.toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell sx={{ py:1.5, fontSize:'0.875rem' }}>BlitzPay Services</TableCell>
                      <TableCell align="right" sx={{ py:1.5, fontSize:'0.875rem' }}>${amountDue.toFixed(2)}</TableCell>
                      <TableCell align="center" sx={{ py:1.5, fontSize:'0.875rem' }}>1</TableCell>
                      <TableCell align="right" sx={{ py:1.5, fontWeight:600, fontSize:'0.875rem' }}>${amountDue.toFixed(2)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── TOTALS ───────────────────────────────────────────────── */}
            <Grid container justifyContent="flex-end" sx={{ mb:3 }}>
              <Grid item xs={12} sm={6} md={5}>
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">${displayedSubtotal.toFixed(2)}</Typography>
                  </Stack>
                  {effectiveBlitzPayFee > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">{blitzPayFeeLabel}</Typography>
                      <Typography variant="body2">${effectiveBlitzPayFee.toFixed(2)}</Typography>
                    </Stack>
                  )}
                  {parseMoney(customDetails?.rail_fee_in_invoice ?? customDetails?.rail_fee_amount) > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        {`Rail Fee (${customDetails.forced_payment_rail}${customDetails.rail_fee_pct ? ` ${customDetails.rail_fee_pct}%` : ''})${
                          customDetails.rail_fee_payer === 'creator' ? ' — Creator covers' : ''
                        }`}
                      </Typography>
                      <Typography variant="body2">${parseMoney(customDetails.rail_fee_in_invoice ?? customDetails.rail_fee_amount).toFixed(2)}</Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Tax</Typography>
                    <Typography variant="body2">$0.00</Typography>
                  </Stack>
                  <Divider sx={{ my:0.5 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={700}>Total</Typography>
                    <Typography variant="body2" fontWeight={700}>${amountDue.toFixed(2)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Amount Paid</Typography>
                    <Typography variant="body2">${amountPaid.toFixed(2)}</Typography>
                  </Stack>
                  <Divider sx={{ my:0.5 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" sx={{ color:'#ef4444', fontWeight:700 }}>Amount Due (USD)</Typography>
                    <Typography variant="body1" sx={{ color:'#ef4444', fontWeight:700 }}>${outstanding.toFixed(2)}</Typography>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            {/* ── TERMS & BANKING INFO ──────────────────────────────────── */}
            <Box
              sx={{
                mt:3,
                pt:2,
                borderTop:'1px solid #e5e7eb',
                '@media print': {
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight:700, color:'#1e293b', display:'block', mb:1 }}>Terms</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display:'block', mb:1, whiteSpace:'pre-line' }}>
                {invoiceTerms || `Make all checks payable to "${mergedData.billedFrom.display_name}"`}
              </Typography>

              {invoiceNotes && (
                <Typography variant="caption" color="textSecondary" sx={{ display:'block', mb:1, whiteSpace:'pre-line' }}>
                  {invoiceNotes}
                </Typography>
              )}
              {shouldShowStandaloneInvoiceNote && (
                <Box sx={{ mb:1 }}>
                  <Typography variant="caption" sx={{ fontWeight:700, color:'#1e293b', display:'block' }}>
                    Invoice Note
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display:'block', whiteSpace:'pre-line' }}>
                    {invoiceNoteValue}
                  </Typography>
                </Box>
              )}

              {/* Bank Details */}
              {(mergedData.bank.bank_name || mergedData.bank.account_number || mergedData.bank.routing_number) && (
                <Box
                  sx={{
                    mt:2,
                    p:2,
                    bgcolor:'#f8fafc',
                    borderRadius:1,
                    border:'1px solid #e5e7eb',
                    '@media print': {
                      breakInside: 'avoid',
                      pageBreakInside: 'avoid',
                    },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight:700, color:'#1e293b', display:'block', mb:1 }}>
                    Wire / ACH Payment Details
                  </Typography>
                  <Stack spacing={0.3}>
                    {mergedData.bank.account_holder && (
                      <Typography variant="caption" color="textSecondary">
                        Account Holder: <strong>{mergedData.bank.account_holder}</strong>
                      </Typography>
                    )}
                    {mergedData.bank.account_name && (
                      <Typography variant="caption" color="textSecondary">
                        Account Name: <strong>{mergedData.bank.account_name}</strong>
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      Address: {mergedData.billedFrom.address_line1}, {mergedData.billedFrom.city}, {mergedData.billedFrom.state} {mergedData.billedFrom.zip_code}
                    </Typography>
                    {mergedData.bank.bank_name && (
                      <Typography variant="caption" color="textSecondary">
                        Bank: <strong>{mergedData.bank.bank_name}</strong>
                      </Typography>
                    )}
                    {mergedData.bank.bank_address && (
                      <Typography variant="caption" color="textSecondary">
                        Bank Address: {mergedData.bank.bank_address}
                      </Typography>
                    )}
                    {mergedData.bank.account_number && (
                      <Typography variant="caption" color="textSecondary">
                        Account #: {mergedData.bank.account_number}
                      </Typography>
                    )}
                    {mergedData.bank.routing_number && (
                      <Typography variant="caption" color="textSecondary">
                        ABA / Routing / IBAN: {mergedData.bank.routing_number}
                      </Typography>
                    )}
                    {mergedData.bank.swift_code && (
                      <Typography variant="caption" color="textSecondary">
                        SWIFT: {mergedData.bank.swift_code}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Approval Stamp */}
          {invoice?.approval_proof && (
            <Box
              id="approval-stamp-section"
              sx={{
                width:{ xs:'95%', sm:'210mm' },
                maxWidth:'100%',
                m:'16px auto 0',
                '@media print':{ pageBreakBefore:'always', mt:0 },
              }}
            >
              <ApprovalStampDrawer approvalInfo={invoice.approval_proof} invoice={invoice} />
            </Box>
          )}

          {/* Payment CTA */}
          {outstanding > 0 && (
            <Box sx={{ width:{ xs:'95%', sm:'210mm' }, maxWidth:'100%', m:'16px auto 0' }}>
              <PaymentCTA invoice={invoice} invoiceAmount={outstanding} variant="blitzpay" />
            </Box>
          )}
        </Box>

        <HomeFooter />
      </Box>
    </ThemeProvider>
  );
};

export default BlitzPayInvoiceDesign;
